import { useCallback, useEffect, useMemo, useState } from "react";
import { getLegacyUserIdsByTag } from "@/apis/tags/tag-apis";
import { FILTER_BY_TAG } from "@/constants/filterConst";
import { strings } from "@/I18n";
import { type ToastVariant, useToast } from "@/v2/ui/composed/toast";

interface TagFilterResult {
  resolvedUserIds: number[] | null;
  isLoading: boolean;
  totalCount: number;
  refresh: () => void;
}

interface TagFilterValue {
  ids?: string[];
  match?: "AND" | "OR";
}

interface FilterItem {
  filterBy: string;
  filter?: unknown;
}

interface UseTagFilterProps {
  filterValue?: FilterItem[];
}

/**
 * Custom hook for resolving tag filters to user IDs
 *
 * Extracts tag filter from filterProps and resolves the tag IDs to actual user IDs
 * by calling the backend API. Handles loading state and provides a refresh mechanism.
 *
 * @param filterProps - Object containing filterValue array with filter items
 * @returns Object with resolvedUserIds, isLoading, totalCount, and refresh function
 */
export function useTagFilter(filterProps: UseTagFilterProps): TagFilterResult {
  const [resolvedTagUserIds, setResolvedTagUserIds] = useState<number[] | null>(
    null
  );
  const [tagFilterLoading, setTagFilterLoading] = useState(false);
  const [tagFilterTotalCount, setTagFilterTotalCount] = useState(0);
  const [tagFilterRefresh, setTagFilterRefresh] = useState(0);

  const { toast } = useToast();
  const showToast = useCallback(
    (message: string, variant: ToastVariant = "default") => {
      toast(message, variant);
    },
    [toast]
  );

  const tagFilterItem = useMemo(() => {
    return (
      filterProps.filterValue?.find((f) => f.filterBy === FILTER_BY_TAG) || null
    );
  }, [filterProps.filterValue]);

  const tagFilterData = useMemo((): { ids: string[]; match: "AND" | "OR" } => {
    if (!tagFilterItem) return { ids: [], match: "OR" };
    const filter = tagFilterItem.filter;
    let ids: string[] = [];
    let match: "AND" | "OR" = "OR";

    if (
      typeof filter === "object" &&
      filter !== null &&
      !Array.isArray(filter)
    ) {
      const tagFilter = filter as TagFilterValue;
      ids = (tagFilter.ids || []).map(String);
      match = tagFilter.match === "AND" ? "AND" : "OR";
    } else if (Array.isArray(filter)) {
      ids = filter.map(String);
    } else if (filter) {
      ids = [String(filter)];
    }
    return { ids, match };
  }, [tagFilterItem]);

  useEffect(() => {
    if (!tagFilterItem) {
      setResolvedTagUserIds(null);
      setTagFilterLoading(false);
      setTagFilterTotalCount(0);
      return;
    }

    const abortController = new AbortController();
    const tagFilterValues = tagFilterData.ids;

    const resolveTagFilter = async () => {
      setTagFilterLoading(true);
      try {
        const result = await getLegacyUserIdsByTag(
          tagFilterValues,
          tagFilterData.match
        );
        if (!abortController.signal.aborted) {
          const userIds = result.legacyUserIds
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id));
          setResolvedTagUserIds(userIds);
          setTagFilterTotalCount(result.totalCount);
        }
      } catch {
        if (!abortController.signal.aborted) {
          setResolvedTagUserIds([]);
          setTagFilterTotalCount(0);
          showToast(strings("errors.failedToLoadTagFilter"), "error");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setTagFilterLoading(false);
        }
      }
    };

    resolveTagFilter();

    return () => {
      abortController.abort();
    };
  }, [tagFilterItem, tagFilterData, tagFilterRefresh, showToast]);

  const refresh = () => {
    setTagFilterRefresh((prev) => prev + 1);
  };

  return {
    resolvedUserIds: resolvedTagUserIds,
    isLoading: tagFilterLoading,
    totalCount: tagFilterTotalCount,
    refresh,
  };
}
